import type { Neighbor, TripMode, SegmentMode } from '@/types';
import { GRAPH } from '@/data/graph';
import { NIGHT_BONUS_FACTOR, NIGHT_SEGMENT_MIN_DURATION } from './constants';

/**
 * Helpers pour calculer le coût d'une arête selon le mode demandé.
 *
 * Distinction importante :
 * - `getCost`     → coût RÉEL en minutes (pour l'affichage utilisateur)
 * - `getDijkstraCost` → coût PERÇU (priorisation algo), applique le bonus train de nuit
 *
 * Cette distinction permet à l'algo de préférer un train de nuit
 * (coût perçu réduit) tout en affichant la durée réelle à l'utilisateur.
 */

/**
 * Coût réel d'une arête en minutes, selon le mode demandé.
 * `null` si le mode n'est pas disponible sur cette arête.
 *
 * - `train` / `night` → uniquement `edge.train`
 * - `bus`             → uniquement `edge.bus`
 * - `best`            → le minimum disponible
 *
 * Note sur le mode `night` : on prend `edge.train` uniquement. Les bus de nuit
 * (`edge.nightBus`) sont signalés visuellement dans la carte mais ne sont PAS
 * privilégiés par l'algo en mode nuit — l'utilisateur qui veut un confort
 * couchette s'attend à un train, pas à un FlixBus de nuit.
 */
export function getCost(edge: Neighbor, mode: TripMode): number | null {
  if (mode === 'train' || mode === 'night') return edge.train;
  if (mode === 'bus') return edge.bus;
  // mode === 'best'
  if (edge.train != null && edge.bus != null) return Math.min(edge.train, edge.bus);
  return edge.train ?? edge.bus;
}

/**
 * Quel mode est effectivement utilisé sur cette arête, en mode donné.
 * `null` si aucun mode disponible.
 */
export function getUsedMode(edge: Neighbor, mode: TripMode): SegmentMode | null {
  if (mode === 'train' || mode === 'night') return edge.train != null ? 'train' : null;
  if (mode === 'bus') return edge.bus != null ? 'bus' : null;
  // mode === 'best'
  if (edge.train != null && edge.bus != null) {
    return edge.train <= edge.bus ? 'train' : 'bus';
  }
  return edge.train != null ? 'train' : 'bus';
}

/**
 * Coût "perçu" — celui qui pilote la priorité dans Dijkstra.
 *
 * En mode `night`, les TRAINS de nuit dont la durée réelle est ≥ 6h voient
 * leur coût réduit par NIGHT_BONUS_FACTOR pour que l'algo les préfère.
 *
 * Pourquoi le seuil 6h ?
 *   - Sur un trajet < 6h, on ne dort pas vraiment (le voyage commence après
 *     22h et finit avant 4h du mat). Le bonus n'a aucun sens.
 *   - Les vrais Nightjet/European Sleeper sont tous ≥ 8h. Les "trains de nuit"
 *     plus courts dans les Balkans sont en réalité des trains de fin de soirée
 *     qu'on prend pour gagner du temps, pas pour économiser une nuit d'hôtel.
 *
 * Les bus de nuit ne reçoivent JAMAIS de bonus : on les liste pour l'utilisateur
 * mais on ne les pousse pas algorithmiquement (Q1 du tri : "filtrer les bus
 * séparément du flag train").
 *
 * Le coût réel (pour l'affichage) reste inchangé.
 */
export function getDijkstraCost(edge: Neighbor, mode: TripMode): number | null {
  const raw = getCost(edge, mode);
  if (raw == null) return null;
  if (
    mode === 'night' &&
    edge.night &&
    edge.train != null &&
    edge.train >= NIGHT_SEGMENT_MIN_DURATION
  ) {
    return Math.round(raw * NIGHT_BONUS_FACTOR);
  }
  return raw;
}

// =====================================================================
// Détection des modes disponibles sur un segment direct A → B
// =====================================================================

/**
 * Renvoie l'arête directe entre `from` et `to` si elle existe, sinon `null`.
 * Le graphe étant non-orienté, on lit côté `from` et on filtre sur `to`.
 */
function findDirectEdge(from: string, to: string): Neighbor | null {
  const neighbors = GRAPH[from];
  if (!neighbors) return null;
  for (const n of neighbors) {
    if (n.to === to) return n;
  }
  return null;
}

/**
 * Indique si une arête DIRECTE entre deux villes permet à l'utilisateur
 * de choisir entre train et bus.
 *
 * Renvoie `false` :
 * - s'il n'y a pas d'arête directe (= itinéraire nécessite des correspondances :
 *   l'override de mode global ne s'applique pas finement à chaque sous-segment,
 *   et on évite de proposer un choix qui pourrait casser le routage)
 * - si un seul des deux modes est disponible
 *
 * Renvoie `true` uniquement si `edge.train != null` ET `edge.bus != null`.
 */
export function canChooseModeBetween(from: string, to: string): boolean {
  const edge = findDirectEdge(from, to);
  if (!edge) return false;
  return edge.train != null && edge.bus != null;
}
